import axios from 'axios';
import apiConfig from '../config/api.config';

/**
 * Service for interacting with the ESDL monitoring API endpoints
 */
const esdlService = {
  /**
   * Get ESDL monitoring data with optional filters
   * @param {Object} filters - Filter options for the query
   * @returns {Promise} - The API response
   */
  getMonitoringData: async (filters = {}) => {
    try {
      console.log('Fetching ESDL monitoring data with filters:', filters);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/monitoring`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/monitoring`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('ESDL monitoring data response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching ESDL monitoring data:', error);
      throw error;
    }
  },

  /**
   * Get ESDL monitoring statistics and aggregated data
   * @returns {Promise} - The API response
   */
  getMonitoringStats: async () => {
    try {
      console.log('Fetching ESDL monitoring stats');
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/monitoring/stats`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/monitoring/stats`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('ESDL monitoring stats response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching ESDL monitoring stats:', error);
      throw error;
    }
  },

  /**
   * Get ESDL error logs with optional filters
   * @param {Object} filters - Filter options for the query
   * @returns {Promise} - The API response
   */
  getErrorLogs: async (filters = {}) => {
    try {
      console.log('Fetching ESDL error logs with filters:', filters);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/monitoring/errors`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/monitoring/errors`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('ESDL error logs response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching ESDL error logs:', error);
      throw error;
    }
  },

  /**
   * Get event statistics (received, processed, dropped)
   * @param {Object} filters - Filter options for the query (region, application, range)
   * @returns {Promise} - The API response
   */
  getEventStats: async (filters = {}) => {
    try {
      console.log('Fetching event statistics with filters:', filters);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/events/stats`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/events/stats`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Event statistics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching event statistics:', error);
      throw error;
    }
  },

  /**
   * Get system resource utilization (CPU, memory, disk)
   * @param {Object} filters - Filter options for the query (region, hostname, range)
   * @returns {Promise} - The API response
   */
  getSystemResources: async (filters = {}) => {
    try {
      console.log('Fetching system resources with filters:', filters);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/system/resources`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/system/resources`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('System resources response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching system resources:', error);
      throw error;
    }
  },

  /**
   * Get time series data for monitoring metrics
   * @param {Object} params - Parameters for the query (region, hostname, metric, from_date, to_date, interval)
   * @returns {Promise} - The API response
   */
  getMonitoringTimeseries: async (params = {}) => {
    try {
      console.log('Fetching monitoring timeseries with params:', params);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/monitoring/timeseries`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/monitoring/timeseries`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Monitoring timeseries response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching monitoring timeseries:', error);
      throw error;
    }
  },

  /**
   * Get list of all regions with host counts
   * @returns {Promise} - The API response
   */
  getRegions: async () => {
    try {
      console.log('Fetching regions');
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/regions`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/regions`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Regions response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching regions:', error);
      throw error;
    }
  },

  /**
   * Get list of all hosts with basic metrics
   * @param {Object} filters - Filter options for the query (region, application)
   * @returns {Promise} - The API response
   */
  getHosts: async (filters = {}) => {
    try {
      console.log('Fetching hosts with filters:', filters);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/hosts`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/hosts`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Hosts response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching hosts:', error);
      throw error;
    }
  },
  
  /**
   * Get dashboard metrics
   * @param {Object} filters - Filter options for the query (region, hostname)
   * @returns {Promise} - The API response
   */
  getDashboardMetrics: async (filters = {}) => {
    try {
      console.log('Fetching dashboard metrics with filters:', filters);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/dashboard`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/dashboard`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Dashboard metrics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  },
  
  /**
   * Run diagnostics for the ESDL monitoring system
   * @returns {Promise} - The API response
   */
  runDiagnostics: async () => {
    try {
      console.log('Running ESDL monitoring diagnostics');
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/diagnostics`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/diagnostics`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Diagnostics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error running diagnostics:', error);
      throw error;
    }
  },
  
  /**
   * Get service status across all hosts
   * @param {Object} filters - Filter options for the query (region)
   * @returns {Promise} - The API response
   */
  getServicesStatus: async (filters = {}) => {
    try {
      console.log('Fetching services status with filters:', filters);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/services/status`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/services/status`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('Services status response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching services status:', error);
      throw error;
    }
  },

  /**
   * Get OpenSearch cluster health metrics
   * @param {Object} filters - Filter options for the query (region, cluster_name)
   * @returns {Promise} - The API response
   */
  getOpenSearchHealth: async (filters = {}) => {
    try {
      console.log('Fetching OpenSearch health metrics with filters:', filters);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/opensearch/health`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/opensearch/health`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('OpenSearch health metrics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching OpenSearch health metrics:', error);
      if (error.response && error.response.status === 404) {
        // Return empty data structure if no data found
        return {
          cluster_name: filters.cluster_name || '',
          region: filters.region || '',
          status: 'unknown',
          number_of_nodes: 0,
          number_of_data_nodes: 0,
          active_primary_shards: 0,
          active_shards: 0,
          relocating_shards: 0,
          initializing_shards: 0,
          unassigned_shards: 0,
          delayed_unassigned_shards: 0,
          number_of_pending_tasks: 0,
          task_max_waiting_in_queue_millis: 0
        };
      }
      throw error;
    }
  },
  
  /**
   * Get OpenSearch node metrics
   * @param {Object} filters - Filter options for the query (region, hostname, cluster_name)
   * @returns {Promise} - The API response
   */
  getOpenSearchNodeMetrics: async (filters = {}) => {
    try {
      console.log('Fetching OpenSearch node metrics with filters:', filters);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/opensearch/nodes`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/opensearch/nodes`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('OpenSearch node metrics response:', response.data);
      
      // Ensure consistent data structure
      if (!response.data.nodes) {
        response.data.nodes = [];
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching OpenSearch node metrics:', error);
      return { nodes: [] };
    }
  },
  
  /**
   * Get OpenSearch indices metrics
   * @param {Object} filters - Filter options for the query (region, cluster_name)
   * @returns {Promise} - The API response
   */
  getOpenSearchIndicesMetrics: async (filters = {}) => {
    try {
      console.log('Fetching OpenSearch indices metrics with filters:', filters);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/opensearch/indices`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/opensearch/indices`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('OpenSearch indices metrics response:', response.data);
      
      // Ensure consistent data structure
      if (!response.data.indices) {
        response.data.indices = [];
      }
      if (!response.data.total_indices) {
        response.data.total_indices = 0;
      }
      if (!response.data.total_docs) {
        response.data.total_docs = 0;
      }
      if (!response.data.total_size_bytes) {
        response.data.total_size_bytes = 0;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching OpenSearch indices metrics:', error);
      return {
        indices: [],
        total_indices: 0,
        total_docs: 0,
        total_size_bytes: 0
      };
    }
  },
  
  /**
   * Get OpenSearch timeseries data
   * @param {Object} params - Parameters for the query (region, cluster_name, metric, from_date, to_date, interval)
   * @returns {Promise} - The API response
   */
  getOpenSearchTimeseries: async (params = {}) => {
    try {
      console.log('Fetching OpenSearch timeseries with params:', params);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/opensearch/timeseries`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/opensearch/timeseries`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('OpenSearch timeseries response:', response.data);
      
      // Enhanced debugging for timeseries data
      const metric = params.metric || 'cpu';
      if (!response.data[metric]) {
        console.warn(`Metric '${metric}' not found in response data:`, response.data);
        response.data[metric] = {
          timestamps: [],
          values: []
        };
      } else {
        console.log(`Found ${response.data[metric].timestamps.length} data points for metric '${metric}'`);
        if (response.data[metric].timestamps.length > 0) {
          console.log('Sample data point:', {
            timestamp: response.data[metric].timestamps[0],
            value: response.data[metric].values[0]
          });
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching OpenSearch timeseries:', error);
      console.error('Error details:', error.response?.data || error.message);
      const metric = params.metric || 'cpu';
      return {
        [metric]: {
          timestamps: [],
          values: []
        },
        range: params.range || '1h',
        cluster_name: params.cluster_name || '',
        region: params.region || ''
      };
    }
  },
  
  /**
   * Get OpenSearch cluster overview
   * @param {Object} filters - Filter options for the query (region)
   * @returns {Promise} - The API response
   */
  getOpenSearchOverview: async (filters = {}) => {
    try {
      console.log('Fetching OpenSearch overview with filters:', filters);
      console.log('API endpoint:', `${apiConfig.API_BASE_URL}/api/esdl/opensearch/overview`);

      const response = await axios.get(`${apiConfig.API_BASE_URL}/api/esdl/opensearch/overview`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('OpenSearch overview response:', response.data);
      
      // Ensure consistent data structure
      if (!response.data.clusters) {
        response.data.clusters = [];
      }
      if (!response.data.clusters_count) {
        response.data.clusters_count = 0;
      }
      if (!response.data.total_nodes) {
        response.data.total_nodes = 0;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching OpenSearch overview:', error);
      return {
        clusters: [],
        clusters_count: 0,
        total_nodes: 0,
        total_data_nodes: 0,
        total_indices: 0,
        total_shards: 0,
        total_primary_shards: 0,
        total_documents: 0,
        total_store_size_bytes: 0,
        avg_cpu_percent: 0,
        avg_memory_percent: 0,
        avg_heap_percent: 0,
        timestamp: new Date().toISOString()
      };
    }
  }
};

export default esdlService; 